"use client";

import { Link, Typography } from "@shared/ui.primitives";
import React, { useCallback, Fragment } from "react";
import * as styles from "./style.css";
import { navigate } from "@shared/utils.routing";
import { dispatch } from "@shared/utils.events";
import { useScrollDirection } from "hooks/useScrollDirection";
import { ExperimentFlags } from "@shared/utils.experimentation";
import clsx from "clsx";

interface NavItem {
  id: string;
  label: string;
  url: string;
  isQuiz?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { id: "quiz", label: "Quiz", url: "/quiz/skincare", isQuiz: true },
  { id: "new-in", label: "New In", url: "/shop/whatsnew" },
  { id: "bestsellers", label: "Bestsellers", url: "/shop/bestsellers" },
  { id: "skincare", label: "Skincare", url: "/shop/skincare" }
];

const QuizNavItem: React.FC<{ item: NavItem; onClick: () => void }> = ({
  item,
  onClick
}) => (
  <div
    data-testId="quiz-with-animation"
    className={styles.quizLinkContainer}
    onClick={onClick}
  >
    <Typography underline={false} colour="secondary-light" variant="body-sm">
      {item.label}
    </Typography>
    <div className={styles.quizLink} />
  </div>
);

const RegularNavItem: React.FC<{ item: NavItem; onClick: () => void }> = ({
  item,
  onClick
}) => (
  <Link href={item.url} noPadding onClick={onClick}>
    <Typography underline={false} colour="primary-medium" variant="link-sm">
      {item.label}
    </Typography>
  </Link>
);

export const MiniNavBar: React.FC<{ quizMiniNavExperimentValue: number }> = ({
  quizMiniNavExperimentValue
}) => {
  const { isVisible, isAtTop } = useScrollDirection();

  const handleNavClick = useCallback((url: string) => {
    dispatch("page element clicked", {
      target: "mini_nav_bar",
      details: url,
      location: "mini_nav_bar"
    });
    navigate(url);
  }, []);

  const containerClasses = clsx(
    styles.container,
    isVisible ? styles.visible : styles.hidden,
    isAtTop ? styles.atTop : ""
  );

  return (
    <div
      data-event-intersection="experimentViewed"
      data-flag={ExperimentFlags.PAGE_EXPERIMENT_QUIZ_MINI_NAV}
      data-variation={quizMiniNavExperimentValue}
      className={containerClasses}
      aria-label="Mini navigation"
    >
      <div className={styles.linksContainer}>
        {NAV_ITEMS.map((item, index) => {
          const NavComponent =
            item.isQuiz && quizMiniNavExperimentValue === 1
              ? QuizNavItem
              : RegularNavItem;

          return (
            <Fragment key={item.id}>
              <NavComponent
                item={item}
                onClick={() => handleNavClick(item.url)}
              />
              {index < NAV_ITEMS.length - 1 && (
                <span className={styles.separator} aria-hidden="true" />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};
